/**
 * 邻接、Footprint 展开、占用与空间查询。
 */

import {
  sectorsOfRing,
  cellKey,
  type BuildCell,
  type BuildCellId,
  type BuildLayer,
  type SpatialMapConfig,
  type SpatialMapData,
} from './types.js';
import { getCell, isValidCellId } from './grid.js';

/** Footprint 校验所需的最小建筑形状（避免 simulation-core 依赖 game-data 运行时）。 */
export interface FootprintSpec {
  readonly radial: number;
  readonly angular: number;
}

/** 放置校验所需的最小建筑定义形状。 */
export interface PlacementSpecShape {
  readonly allowedLayers: readonly BuildLayer[];
  readonly footprint: FootprintSpec;
  readonly placement?: {
    readonly maxSlopeBasisPoints?: number;
    readonly minSupport?: number;
    readonly maxContamination?: number;
  };
}

/**
 * 把某环的扇区索引按**角度**投影到另一环。
 *
 * 每环扇区数可以不同，跨环时直接沿用索引会让相邻环错位（外环索引 0 与内环
 * 索引 0 未必同向），道路连通性会在环之间悄悄断开。因此一律按中心角折算。
 */
export function projectSector(
  config: SpatialMapConfig,
  layer: BuildLayer,
  fromRing: number,
  sector: number,
  toRing: number,
): number {
  const nFrom = sectorsOfRing(config, layer, fromRing);
  const nTo = sectorsOfRing(config, layer, toRing);
  if (nFrom === nTo) return ((sector % nTo) + nTo) % nTo;
  const t = (sector + 0.5) / nFrom;              // 归一化角度 [0,1)
  return Math.min(nTo - 1, Math.floor(t * nTo));
}

/** 同层邻接（径向 ±1、环向 ±1，含对角；sector 环绕，跨环按角度投影）。 */
export function neighbors(map: SpatialMapData, id: BuildCellId): BuildCellId[] {
  const out: BuildCellId[] = [];
  const seen = new Set<string>();
  const rings = map.config.ringsPerLayer[id.layer];
  for (let dr = -1; dr <= 1; dr++) {
    const ring = id.ring + dr;
    if (ring < 0 || ring >= rings) continue;
    const n = sectorsOfRing(map.config, id.layer, ring);
    // 同环用索引偏移；跨环先投影再偏移，保证方位一致
    const base = dr === 0 ? id.sector : projectSector(map.config, id.layer, id.ring, id.sector, ring);
    for (let ds = -1; ds <= 1; ds++) {
      if (dr === 0 && ds === 0) continue;
      const sector = ((base + ds) % n + n) % n;
      const key = `${ring}:${sector}`;
      if (seen.has(key)) continue;               // 扇区数变化时投影可能撞车
      seen.add(key);
      out.push({ layer: id.layer, ring, sector });
    }
  }
  return out;
}

/** Footprint 展开：从锚点 cell 按建筑 footprint(radial×angular) 展开到覆盖的所有 cell。 */
export function expandFootprint(
  map: SpatialMapData,
  anchor: BuildCellId,
  radial: number,
  angular: number,
): BuildCellId[] | null {
  const out: BuildCellId[] = [];
  const seen = new Set<string>();
  for (let dr = 0; dr < radial; dr++) {
    const ring = anchor.ring + dr;
    if (ring < 0 || ring >= map.config.ringsPerLayer[anchor.layer]) return null; // 越界
    const n = sectorsOfRing(map.config, anchor.layer, ring);
    const base = dr === 0
      ? anchor.sector
      : projectSector(map.config, anchor.layer, anchor.ring, anchor.sector, ring);
    for (let ds = 0; ds < angular; ds++) {
      const sector = (base + ds) % n;
      const id: BuildCellId = { layer: anchor.layer, ring, sector };
      if (!isValidCellId(map, id)) return null; // 越界
      const key = `${ring}:${sector}`;
      if (seen.has(key)) continue;              // 跨环投影可能落到同一格
      seen.add(key);
      out.push(id);
    }
  }
  return out;
}

export type PlacementRejection =
  | 'BUILD_OUT_OF_BOUNDS'
  | 'BUILD_LAYER_NOT_ALLOWED'
  | 'BUILD_CELL_NOT_BUILDABLE'
  | 'BUILD_CELL_OCCUPIED'
  | 'BUILD_SLOPE_TOO_STEEP'
  | 'BUILD_SUPPORT_TOO_LOW'
  | 'BUILD_CONTAMINATION_TOO_HIGH';

export interface PlacementCheck {
  readonly ok: boolean;
  readonly rejection?: PlacementRejection;
  readonly cells?: readonly BuildCellId[];
}

/** 放置校验（对应 spatial-and-building-system.md 的放置验证顺序之子集）。 */
export function checkPlacement(
  map: SpatialMapData,
  def: PlacementSpecShape,
  anchor: BuildCellId,
): PlacementCheck {
  if (!def.allowedLayers.includes(anchor.layer)) {
    return { ok: false, rejection: 'BUILD_LAYER_NOT_ALLOWED' };
  }
  const radial = def.footprint.radial;
  const angular = def.footprint.angular;
  const cells = expandFootprint(map, anchor, radial, angular);
  if (cells === null) {
    return { ok: false, rejection: 'BUILD_OUT_OF_BOUNDS' };
  }
  const pl = def.placement;
  for (const id of cells) {
    const cell = getCell(map, id);
    if (!cell) return { ok: false, rejection: 'BUILD_OUT_OF_BOUNDS' };
    if (!cell.buildable) return { ok: false, rejection: 'BUILD_CELL_NOT_BUILDABLE', cells };
    if (cell.occupiedBy !== null) return { ok: false, rejection: 'BUILD_CELL_OCCUPIED', cells };
    if (pl?.maxSlopeBasisPoints !== undefined && cell.slopeBasisPoints > pl.maxSlopeBasisPoints)
      return { ok: false, rejection: 'BUILD_SLOPE_TOO_STEEP', cells };
    if (pl?.minSupport !== undefined && cell.support < pl.minSupport)
      return { ok: false, rejection: 'BUILD_SUPPORT_TOO_LOW', cells };
    if (pl?.maxContamination !== undefined && cell.contamination > pl.maxContamination)
      return { ok: false, rejection: 'BUILD_CONTAMINATION_TOO_HIGH', cells };
  }
  return { ok: true, cells };
}

/** 占用一组 cell（放置成功后调用）。返回 false 表示冲突。 */
export function occupyCells(map: SpatialMapData, cells: readonly BuildCellId[], entityId: string): boolean {
  for (const id of cells) {
    const cell = getCell(map, id);
    if (!cell || cell.occupiedBy !== null) return false;
  }
  for (const id of cells) {
    const cell = getCell(map, id);
    if (cell) cell.occupiedBy = entityId;
  }
  return true;
}

/** 释放一组 cell。 */
export function releaseCells(map: SpatialMapData, cells: readonly BuildCellId[]): void {
  for (const id of cells) {
    const cell = getCell(map, id);
    if (cell) cell.occupiedBy = null;
  }
}

/** 空间查询：某层内满足条件的所有 cell（稳定顺序）。 */
export function queryCells(
  map: SpatialMapData,
  layer: BuildLayer,
  predicate: (cell: BuildCell) => boolean,
): BuildCell[] {
  const out: BuildCell[] = [];
  const rings = map.config.ringsPerLayer[layer];
  for (let ring = 0; ring < rings; ring++) {
    const sectors = sectorsOfRing(map.config, layer, ring);
    for (let sector = 0; sector < sectors; sector++) {
      const cell = map.cells[cellKey({ layer, ring, sector })];
      if (cell && predicate(cell)) out.push(cell);
    }
  }
  return out;
}

/** 统计地图 cell 总数。 */
export function cellCount(map: SpatialMapData): number {
  return Object.keys(map.cells).length;
}
